import { useEffect, useState } from "react";
import { Briefcase, Plus, Pencil, Trash2, X, Star } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";

const EMPTY = {
  slug: "", title: "", category: "", client: "", summary: "", cover_image: "",
  challenge: "", solution: "", features: "", technologies: "", results: "",
  metric_value: "", metric_label: "", order: 0, featured: false,
};

const toForm = (c) => ({
  ...c,
  features: (c.features || []).join("\n"),
  technologies: (c.technologies || []).join(", "),
  results: (c.results || []).join("\n"),
});

const toPayload = (f) => ({
  slug: f.slug.trim(),
  title: f.title, category: f.category, client: f.client, summary: f.summary,
  cover_image: f.cover_image, challenge: f.challenge, solution: f.solution,
  features: f.features.split("\n").map((s) => s.trim()).filter(Boolean),
  technologies: f.technologies.split(",").map((s) => s.trim()).filter(Boolean),
  results: f.results.split("\n").map((s) => s.trim()).filter(Boolean),
  metric_value: f.metric_value, metric_label: f.metric_label,
  order: Number(f.order) || 0, featured: !!f.featured,
});

export default function AdminCaseStudies() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // null | "new" | slug
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/case-studies").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY); setEditing("new"); };
  const openEdit = (c) => { setForm(toForm(c)); setEditing(c.slug); };
  const close = () => setEditing(null);
  const u = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = toPayload(form);
      if (editing === "new") await api.post("/admin/case-studies", payload);
      else await api.put(`/admin/case-studies/${editing}`, payload);
      toast.success("Case study saved");
      close();
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (slug) => {
    if (!window.confirm("Delete this case study?")) return;
    await api.delete(`/admin/case-studies/${slug}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div data-testid="admin-case-studies">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <Briefcase className="w-5 h-5 text-primary" />
          <h2 className="font-heading text-xl font-semibold">Case Studies ({items.length})</h2>
        </div>
        <button onClick={openNew} data-testid="add-case-study" className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-white text-sm font-semibold px-4 py-2.5 transition-colors">
          <Plus className="w-4 h-4" /> New Case Study
        </button>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {items.map((c) => (
          <div key={c.slug} className="border border-white/10 bg-[#0F111A] p-5 flex gap-4">
            <img src={c.cover_image} alt="" className="w-20 h-20 object-cover border border-white/10 shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-white truncate">{c.title}</h3>
                {c.featured && <Star className="w-3.5 h-3.5 text-amber-400 shrink-0" fill="currentColor" />}
              </div>
              <p className="text-xs text-slate-500 mt-0.5">{c.category} · /{c.slug}</p>
              <p className="text-sm text-slate-400 mt-2 line-clamp-2">{c.summary}</p>
              <div className="flex gap-2 mt-3">
                <button onClick={() => openEdit(c)} data-testid={`edit-case-${c.slug}`} className="inline-flex items-center gap-1.5 text-xs border border-white/10 px-3 py-1.5 text-slate-300 hover:border-primary hover:text-primary transition-colors"><Pencil className="w-3 h-3" /> Edit</button>
                <button onClick={() => remove(c.slug)} data-testid={`delete-case-${c.slug}`} className="inline-flex items-center gap-1.5 text-xs border border-white/10 px-3 py-1.5 text-slate-400 hover:border-red-400/40 hover:text-red-400 transition-colors"><Trash2 className="w-3 h-3" /> Delete</button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm overflow-y-auto" onClick={close}>
          <div className="min-h-full grid place-items-center p-4">
            <form onClick={(e) => e.stopPropagation()} onSubmit={save} data-testid="case-form" className="w-full max-w-2xl bg-[#0F111A] border border-white/10 p-7 space-y-4 my-8">
              <div className="flex items-center justify-between">
                <h3 className="font-heading text-xl font-semibold">{editing === "new" ? "New" : "Edit"} Case Study</h3>
                <button type="button" onClick={close} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <In label="Slug *"><input required disabled={editing !== "new"} value={form.slug} onChange={u("slug")} className={ic} placeholder="my-case-study" /></In>
                <In label="Order"><input type="number" value={form.order} onChange={u("order")} className={ic} /></In>
              </div>
              <In label="Title *"><input required value={form.title} onChange={u("title")} className={ic} /></In>
              <div className="grid sm:grid-cols-2 gap-4">
                <In label="Category"><input value={form.category} onChange={u("category")} className={ic} /></In>
                <In label="Client"><input value={form.client} onChange={u("client")} className={ic} /></In>
              </div>
              <In label="Summary"><textarea rows={2} value={form.summary} onChange={u("summary")} className={ic} /></In>
              <In label="Cover image URL"><input value={form.cover_image} onChange={u("cover_image")} className={ic} placeholder="https://..." /></In>
              <In label="Challenge"><textarea rows={3} value={form.challenge} onChange={u("challenge")} className={ic} /></In>
              <In label="Solution"><textarea rows={3} value={form.solution} onChange={u("solution")} className={ic} /></In>
              <In label="Features (one per line)"><textarea rows={3} value={form.features} onChange={u("features")} className={ic} /></In>
              <In label="Technologies (comma separated)"><textarea rows={2} value={form.technologies} onChange={u("technologies")} className={ic} /></In>
              <In label="Results (one per line)"><textarea rows={3} value={form.results} onChange={u("results")} className={ic} /></In>
              <div className="grid sm:grid-cols-2 gap-4">
                <In label="Metric value"><input value={form.metric_value} onChange={u("metric_value")} className={ic} placeholder="60%" /></In>
                <In label="Metric label"><input value={form.metric_label} onChange={u("metric_label")} className={ic} placeholder="Less manual effort" /></In>
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-300">
                <input type="checkbox" checked={form.featured} onChange={u("featured")} className="accent-[#0096DA] w-4 h-4" /> Feature on homepage
              </label>
              <div className="flex gap-3 pt-2">
                <button type="submit" disabled={saving} data-testid="case-form-submit" className="bg-primary hover:bg-primary/90 text-white font-semibold px-6 py-3 transition-colors disabled:opacity-60">{saving ? "Saving..." : "Save"}</button>
                <button type="button" onClick={close} className="border border-white/15 text-slate-300 px-6 py-3 hover:border-white/40 transition-colors">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

const ic = "w-full bg-[#05060A] border border-white/10 focus:border-primary outline-none text-white px-3.5 py-2.5 text-sm transition-colors disabled:opacity-50";
const In = ({ label, children }) => (
  <label className="block"><span className="block text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1.5">{label}</span>{children}</label>
);
